import { useMutation } from '@apollo/client';
import { useCallback, useEffect } from 'react';

import { SendWebConsentDataDocument } from './send-web-consent-mutation.gql-types';
import type { UCUIEventDetail } from './usercentrics.types';

const isUsercentricsAvailable = (): boolean | undefined => {
  return (
    typeof window !== 'undefined' &&
    window.__ucCmp &&
    typeof window.__ucCmp.getConsentDetails === 'function'
  );
};

// Once a user clicks on the "Accept all" or "Deny all" button in the Usercentrics UI,
// the consent data is sent to the backend.
export const useUsercentricsEvent = (): void => {
  const [sendWebConsent] = useMutation(SendWebConsentDataDocument);

  const handleUCUICMPEvent = useCallback(
    (event: CustomEvent<UCUIEventDetail>): void => {
      if (!isUsercentricsAvailable()) {
        return;
      }

      const type = event?.detail?.type;

      const sendConsentData = async (): Promise<void> => {
        try {
          const consentDetails = await window.__ucCmp!.getConsentDetails();

          const services = Object.entries(consentDetails.services).map(
            ([id, service]) => ({
              id,
              name: service.name,
              consent: {
                status: service.consent.given ? 'ACCEPTED' : 'DENIED',
              },
            })
          );

          const tcfVendors = window.__ucCmp!.cmpController?.tcf.vendors ?? {};

          const payload = {
            channel: 'web',
            domain: window.location.origin,
            controllerId: consentDetails.consent.controllerId,
            nonTcfVendors: JSON.stringify(services),
            tcfVendors: JSON.stringify(
              Object.values(tcfVendors).map(({ id, name, consent }) => ({
                id,
                name,
                consent,
              }))
            ),
          };

          console.log('sending web consent data');
          sendWebConsent({ variables: { input: payload } });
        } catch (error) {
          console.error('Failed to get consent details:', error);
        }
      };

      switch (type) {
        case 'SAVE':
        case 'DENY_ALL':
        case 'ACCEPT_ALL':
          void sendConsentData();
          break;
        default:
          break;
      }
    },
    [sendWebConsent]
  );

  useEffect(() => {
    window.addEventListener(
      'UC_UI_CMP_EVENT',
      handleUCUICMPEvent as EventListener
    );

    return () => {
      window.removeEventListener(
        'UC_UI_CMP_EVENT',
        handleUCUICMPEvent as EventListener
      );
    };
  }, [handleUCUICMPEvent]);
};
